import React, { useState } from 'react';
import { EnhancedChatMessage, Citation } from '../../types/educational-chat';
import VidyaAvatar from './VidyaAvatar';
import CitationCard from './CitationCard';
import { useAuth } from '../../contexts/AuthContext';
import SparkleIcon from '../SparkleIcon';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { CollapsibleMarkdown } from './CollapsibleMarkdown';
import { PhotoIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { parseMarkdownSections } from '../../utils/markdownSectionParser';
import './ChatMessage.css';

interface ChatMessageProps {
  message: EnhancedChatMessage;
  isStreaming?: boolean;
  onCitationClick?: (citation: Citation) => void;
  useSparklesAvatar?: boolean;
  agentStatus?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onImageClick?: (imageIndex: number) => void;
  onPageLinkClick?: (bookId: string, pageNumber: number) => void;
  onNoteClick?: (noteId: string) => void;
  onTimestampClick?: (sec: number) => void;
  isLastAiMessage?: boolean;
  onExamCheatCodesClick?: () => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isStreaming = false,
  onCitationClick,
  useSparklesAvatar = false,
  agentStatus,
  isExpanded = false,
  onToggleExpand,
  onImageClick,
  onPageLinkClick,
  onNoteClick,
  onTimestampClick,
  isLastAiMessage = false,
  onExamCheatCodesClick
}) => {
  const { user } = useAuth();
  const [showCitations, setShowCitations] = useState(false);

  // Compute exam cheat codes chip visibility.
  // Show ONLY for QA-mode responses — revision output (Concept Review,
  // Speed Run, Full Chapter, Note Review) is already exam-prep format, so
  // the cheat-codes CTA is redundant there.
  //
  // A response is QA-mode if ANY section title matches a QA signature:
  // Answer, Connections, Visual Guide, Your turn, Exam cheat codes.
  // (Follow-up QA responses may open with Connections or Visual Guide,
  // not Answer — so we can't gate on first section alone.)
  const parsed = parseMarkdownSections(message.content_text || '');
  const hasQAMarker = parsed.sections.some((s) => {
    const t = s.title.toLowerCase();
    return (
      t.includes('answer') ||
      t.includes('connection') ||
      t.includes('visual') ||
      t === 'your turn' ||
      t.includes('cheat')
    );
  });
  const showExamChip = message.message_type === 'ai'
    && parsed.hasMultipleSections
    && !isStreaming
    && isLastAiMessage
    && hasQAMarker;

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const parts = user.name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  const getGravatarUrl = (email: string | undefined, size: number = 80) => {
    if (!email) return null;
    // For demo, always return null to force initials display
    return null;
  };

  const gravatarUrl = getGravatarUrl(user?.email);

  if (message.message_type === 'user') {
    const hasAttachments = message.attachments && message.attachments.length > 0;

    return (
      <div className="message-container message-user">
        <div className="message-content-wrapper">
          <div className="message-bubble user-bubble">
            {/* Expand/Minimize button at top - matches AI message pattern */}
            {hasAttachments && onToggleExpand && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand();
                }}
                className="user-attachment-toggle"
              >
                {isExpanded ? (
                  <>
                    <ChevronUpIcon className="user-toggle-icon" />
                    <span>Minimize</span>
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="user-toggle-icon" />
                    <span>{message.attachments!.length} {message.attachments!.length === 1 ? 'attachment' : 'attachments'}</span>
                  </>
                )}
              </button>
            )}

            <p className="message-text">{message.content_text}</p>

            {hasAttachments && !isExpanded && (
              <div className="attachment-preview">
                <div className="attachment-preview-content">
                  <img
                    src={message.attachments![0].data_url || message.attachments![0].file_url}
                    alt="Attachment preview"
                    className="attachment-preview-image"
                  />
                </div>
              </div>
            )}

            {hasAttachments && isExpanded && (
              <div className="message-attachments-expanded">
                <div className="message-attachments-grid">
                  {message.attachments!.map((attachment, index) => (
                    <div key={index} className="message-attachment-item-expanded">
                      <img
                        src={attachment.data_url || attachment.file_url}
                        alt={attachment.file_name || `Attachment ${index + 1}`}
                        className="message-attachment-image-expanded"
                        onClick={(e) => {
                          e.stopPropagation();
                          onImageClick?.(index);
                        }}
                        title="Click to view full size"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="avatar-container">
            {gravatarUrl ? (
              <img
                src={gravatarUrl}
                alt={user?.name || 'User'}
                className="user-avatar"
                onError={(e) => {
                  // Fallback to initials if gravatar fails
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`user-initials ${gravatarUrl ? 'hidden' : ''}`}>
              {getUserInitials()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (message.message_type === 'ai') {
    return (
      <div className="message-container message-ai">
        <div className="message-content-wrapper">
          <div className="avatar-container">
            {useSparklesAvatar ? (
              <div className="ai-avatar">
                {!isStreaming || message.content_text ? (
                  <SparkleIcon size={20} color="#06B6D4" animated={true} />
                ) : null}
              </div>
            ) : (
              <VidyaAvatar
                state={isStreaming ? 'speaking' : 'idle'}
                size={36}
              />
            )}
          </div>

          {/* Show loading state WITHOUT bubble wrapper - matches Dhyana style */}
          {isStreaming && !message.content_text && (
            <div className="streaming-container">
              <div className="streaming-icon">
                <SparkleIcon size={24} color="#06B6D4" animated={true} />
              </div>
              {agentStatus && (
                <span className="agent-status">{agentStatus}</span>
              )}
            </div>
          )}

          {/* Show content in bubble only when we have content */}
          {message.content_text && (
            <div className="message-bubble ai-bubble">
              <div className="ai-response-container">
                <div className="response-content">
                  <CollapsibleMarkdown
                    content={message.content_text}
                    className="markdown-content"
                    isStreaming={isStreaming}
                    onPageLinkClick={onPageLinkClick}
                    onNoteClick={onNoteClick}
                    onTimestampClick={onTimestampClick}
                  />

                  {/* Citations section */}
                  {message.citations && message.citations.length > 0 && (
                    <div className="citations-section">
                      <button
                        onClick={() => setShowCitations(!showCitations)}
                        className="citations-toggle"
                      >
                        <svg className="book-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <span>{message.citations.length} Source{message.citations.length > 1 ? 's' : ''}</span>
                        <svg
                          className={`chevron-icon ${showCitations ? 'rotated' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {showCitations && (
                        <div className="citations-list">
                          {message.citations.map((citation, idx) => (
                            <CitationCard
                              key={idx}
                              citation={citation}
                              index={idx + 1}
                              onClick={() => onCitationClick?.(citation)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Exam cheat codes chip — standalone pill below the message */}
        {showExamChip && (
          <button
            onClick={onExamCheatCodesClick}
            className="exam-cheat-codes-chip"
          >
            Want to explore exam cheat codes for this topic? 🎯
          </button>
        )}
      </div>
    );
  }

  // System message
  return (
    <div className="message-container message-system">
      <div className="system-message">
        <span>{message.content_text}</span>
      </div>
    </div>
  );
};

export default ChatMessage;